import { inject } from '@angular/core';
import { PAGINATION_DEFAULT } from '@core/constants';
import { Installation, InstallationFilter, InstallationsResponse } from '@core/models';
import { InstallationService } from '@core/services/installation.service';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';

type InstallationState = {
  isLoading: boolean;
  installations: InstallationsResponse;
  filter: InstallationFilter;
  isLoadingDetail: boolean;
  detail: Installation | null;
  detailError: boolean;
};

const initialState: InstallationState = {
  isLoading: false,
  installations: { installations: [], numberOfItems: 0 },
  filter: { limit: PAGINATION_DEFAULT.limit, offset: 0 },
  isLoadingDetail: false,
  detail: null,
  detailError: false,
};

type FilterChange = Omit<InstallationFilter, 'limit' | 'offset'>;

// Provided by the page that uses it, so a list or a detail never outlives its page.
export const InstallationStore = signalStore(
  withState(initialState),
  withMethods((store, installationService = inject(InstallationService)) => {
    const load = rxMethod<InstallationFilter>(
      pipe(
        tap(filter => patchState(store, { isLoading: true, filter })),
        switchMap(filter =>
          installationService.list(filter).pipe(
            tapResponse({
              next: installations => patchState(store, { installations, isLoading: false }),
              error: () =>
                patchState(store, {
                  installations: initialState.installations,
                  isLoading: false,
                }),
            }),
          ),
        ),
      ),
    );

    return {
      loadPage: (pageIndex: number) =>
        load({ ...store.filter(), offset: pageIndex * store.filter().limit }),

      applyFilter: (change: FilterChange) => {
        const { limit } = store.filter();
        load({ ...change, limit, offset: 0 });
      },

      loadDetail: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isLoadingDetail: true, detailError: false })),
          switchMap(installationOid =>
            installationService.get(installationOid).pipe(
              tapResponse({
                next: (detail: Installation) =>
                  patchState(store, { detail, isLoadingDetail: false }),
                error: () =>
                  patchState(store, { detail: null, isLoadingDetail: false, detailError: true }),
              }),
            ),
          ),
        ),
      ),
    };
  }),
);
